import Promise from 'bluebird';
import { Bootstrap, GraphQL, PageAPI, SearchAPI } from 'lib/api';
import bootstrapSelector from 'common/selectors/bootstrapSelector';
import apiParamsSelector, {
    graphqlParamsSelector,
    pageAPIParamsSelector,
    pageAPILimitedParamsSelector,
    searchApiParamsSelector,
    suggestApiParamsSelector
} from 'common/selectors/apiParamsSelector';
import { configEnvSelector } from 'common/selectors/appSelector';
import { bamSessionTokenSelector } from 'common/selectors/authenticationSelectors';
import {
    pickerStreamsSelect,
    pickerStreamsSelectError
} from './appActions';
// un-comment to debug multiple feeds scenario
// import getTestFeed from 'lib/api/TestFeeds';

export const SEARCH_TYPES = {
    search: 'SEARCH',
    suggest: 'SUGGEST',
    competitors: 'COMPETITORS',
    listingsByCompetitor: 'LISTINGS_BY_COMPETITOR'
};

function addRecommendedConentIdsToContents(contents, recommendedContentIds = []) {
    // To each content containing `dss` object, we want to expose the `recommendedContentIds` array
    contents.forEach(content => {
        if (content.dss) {
            content.dss.recommendedContentIds = recommendedContentIds;
        }
    });
}

export function addContentToStream(content) {
    const { streams, ...contentParams } = content;

    if (Array.isArray(streams) && streams.length > 0) {
        streams.forEach(stream => {
            // should we just add content id and look up content metadata on demand?
            stream.contentId = content.id;
            stream.content = contentParams;
             // adds dss object to top level content
            if (stream.dss) {
                content.dss = stream.dss;
            }
        });
    }

    return content;
}

export function parseContents(contents) {
    if (!contents) {
        return contents;
    }
    const recommendedContentIds = [];

    if (!Array.isArray(contents)) {
        return addContentToStream(contents);
    }

    contents.forEach(content => {
        addContentToStream(content);
        // NOTE: For each given content containing `dss.contentId`, we want to keep track via
        // recommendedContentIds which is required when executing `sendContentReward()`.
        if (content.dss && content.dss.contentId) {
            recommendedContentIds.push(content.dss.contentId);
        }
    });

    if (recommendedContentIds.length) {
        addRecommendedConentIdsToContents(contents, recommendedContentIds);
    }

    return contents;
}

export function graphql(api, variables) {
    return (dispatch, getState) => {
        const state = getState();
        const { apiEnv, lang, ...params } = graphqlParamsSelector(state);
        return GraphQL[api]({
            apiEnv,
            lang,
            variables: { ...params, ...variables }
        });
    };
}

export function getSearchResults(query, apiEnv, endpoint = SEARCH_TYPES.search, sortBy, sortOrder) {
    if (query.length === 0) {
        return async () => Promise.reject('No Query Entered');
    }
    switch (endpoint) {
        case SEARCH_TYPES.suggest:
            return async (dispatch, getState) => {
                const state = getState();
                const suggestApiParams = suggestApiParamsSelector(state);
                const data = await SearchAPI.getSuggestion(query, apiEnv, { ...suggestApiParams });
                return data;
            };
        case SEARCH_TYPES.competitors:
            return async () => (SearchAPI.getAllCompetitors(apiEnv));
        case SEARCH_TYPES.listingsByCompetitor:
            return async () => (SearchAPI.getEventsForTeam(
                query,
                apiEnv,
                { size: 1000, includes: 'sid', sortBy, sortOrder }));
        case SEARCH_TYPES.search:
        default:
            return async (dispatch, getState) => {
                const state = getState();
                const searchApiParams = searchApiParamsSelector(state);
                const data = await SearchAPI.getSearchResults(query, apiEnv, { ...searchApiParams });
                return data;
            };
    }
}

export function loadBootstrap() {
    return async (dispatch, getState) => {
        const configEnv = configEnvSelector(getState());
        const config = await Bootstrap.loadBootstrap({ configEnv });
        return config;
    };
}

let testFeed; // eslint-disable-line prefer-const;

export function getPage({ name, ...paramsArg }) {
    return async (dispatch, getState) => {
        const state = getState();
        const { pageApi: { pages = [] } = {} } = bootstrapSelector(state);
        const sessionToken = bamSessionTokenSelector(getState());
        const isTopLevel = pages.some(({ id }) => id === name);
        const pageAPIParams = isTopLevel ? pageAPIParamsSelector(state) : pageAPILimitedParamsSelector(state);
        const data = await PageAPI.getPage({
            ...pageAPIParams,
            ...paramsArg,
            name,
            sessionToken
        });

        // un-comment to debug multiple feeds scenario
        // testFeed = getTestFeed('espn3', configEnvSelector(getState()));
        if (data && data.page && data.page.buckets) {
            if (testFeed && data.page.buckets[0].contents[0].streams) {
                data.page.buckets[0].contents[0].streams.push(testFeed);
            }
            data.page.buckets.forEach((bucket) => {
                const { contents } = bucket;
                bucket.contents = parseContents(contents);
            });
        }

        return data;
    };
}

export function getEvent({ query, ...paramsArg }) {
    return async (dispatch, getState) => {
        const state = getState();
        const pageAPIParams = pageAPILimitedParamsSelector(state);
        const data = await PageAPI.getEvent({
            ...pageAPIParams,
            ...paramsArg,
            query
        });
        let notReplays;
        // un-comment to debug multiple feeds scenario
        // testFeed = getTestFeed('espn3', configEnvSelector(getState()));
        if (data && data.page && data.page.contents) {
            if (testFeed) {
                data.page.contents.streams.push(testFeed);
            }
            data.page.contents = parseContents(data.page.contents);

            const content = data.page.contents[0];
            if (content && !query.status) {
                notReplays = content.streams.filter(stream => stream.status !== 'upcoming');
            }
        }

        return notReplays || data;
    };
}

export function getVideo({ pathParams, ...paramsArg }) {
    return async (dispatch, getState) => {
        const state = getState();
        const pageAPIParams = pageAPILimitedParamsSelector(state);
        const data = await PageAPI.getVideo({
            ...pageAPIParams,
            ...paramsArg,
            pathParams
        });

        if (data && data.page && data.page.contents) {
            data.page.contents = parseContents(data.page.contents);
        }

        return data;
    };
}

export function getBucketPage({ bucketId, groupId, ignores, ...paramsArg }) {
    return async (dispatch, getState) => {
        const state = getState();
        const pageAPIParams = pageAPIParamsSelector(state);
        const sessionToken = bamSessionTokenSelector(getState());
        const data = await PageAPI.getBucketPage({
            ...pageAPIParams,
            ...paramsArg,
            bucketId,
            groupId,
            ignores,
            sessionToken
        });

        if (data && data.page && data.page.buckets) {
            data.page.buckets.forEach((bucket) => {
                const { contents } = bucket;
                bucket.contents = parseContents(contents);
            });
        }

        return data;
    };
}

export function getPageWithPathId({ name, pathParams, ...paramsArg }) {
    return async (dispatch, getState) => {
        const state = getState();
        const pageAPIParams = pageAPILimitedParamsSelector(state);
        const sessionToken = bamSessionTokenSelector(getState());
        const data = await PageAPI.getPage({
            ...pageAPIParams,
            ...paramsArg,
            name,
            sessionToken,
            pathParams
        });

        if (data && data.page && data.page.buckets) {
            data.page.buckets.forEach((bucket) => {
                const { contents } = bucket;
                bucket.contents = (contents);
            });
        }

        return data;
    };
}

export function getPageByFullUrl({ url, ...paramsArg }) {
    return async (dispatch, getState) => {
        const state = getState();
        const pageAPIParams = pageAPILimitedParamsSelector(state);
        const sessionToken = bamSessionTokenSelector(getState());
        const data = await PageAPI.getPageByFullUrl({
            ...pageAPIParams,
            ...paramsArg,
            url,
            sessionToken
        });

        if (data && data.page && data.page.buckets) {
            data.page.buckets.forEach((bucket) => {
                const { contents } = bucket;
                bucket.contents = parseContents(contents);
            });
        }

        return data;
    };
}

export function getScoringInfluencers(paramsArg) {
    return (dispatch, getState) => {
        const params = apiParamsSelector(getState());
        return PageAPI.getScoringInfluencers({
            ...params,
            ...paramsArg
        });
    };
}

export function fetchPickerStreams(pickerUrl, eventId) {
    return async (dispatch) => {
        try {
            const data = await PageAPI.getPickerStreams(pickerUrl);
            const { page: { buckets } = {} } = data;

            // NOTE: The `espn-web-player` is written to respect the nested `content` object
            // within the stream object (parent). In order to ensure backwards compatibility,
            // we need to maintain the same structure.
            // This happens when the new `stream picker` is displayed during an AuthZ error state
            // to allow the user to transition to a new stream
            if (Array.isArray(buckets) && buckets.length) {
                buckets.forEach(bucket => {
                    const { contents } = bucket;
                    bucket.contents = parseContents(contents);
                });

                dispatch(pickerStreamsSelect(buckets, eventId));
            } else {
                throw new Error('Picker buckets list is not an array or empty');
            }
            return buckets;
        } catch (error) {
            dispatch(pickerStreamsSelectError(error));
            return [];
        }
    };
}



// WEBPACK FOOTER //
// ./app/common/actions/apiAction.js